import NotificationIcon from "../Icons/NotificationIcon";
import NotificationItemIcon from "../Icons/NotificationItemIcon";
import Dropdown from "./Dropdown";
import {
  GetAllNotificationsAdmin,
  GetAllNotificationsStudent,
} from "../../api/Notifications/GetAllNotifications";
import { useQuery, useMutation } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { useUserDataStore } from "../../stores/user";
import { ReadNotifications } from "../../api/Notifications/ReadNotifications";

const NotificationsDropdown = () => {
  const { userData } = useUserDataStore();
  const userRole = userData?.role;

  const { data: NotificationsResponse, refetch } = useQuery({
    queryFn:
      userRole === "admin"
        ? GetAllNotificationsAdmin
        : GetAllNotificationsStudent,
    queryKey: ["notifications"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const readNotificationsMutation = useMutation<void, Error, void>({
    mutationFn: ReadNotifications,
    onSuccess: () => {
      refetch(); // Refetch notifications after marking them as read
    },
    onError: (error) => {
      console.error("Error reading notifications:", error);
    },
  });

  const handleDropdownClose = () => {
    readNotificationsMutation.mutate(); // Trigger the mutation when dropdown closes
  };

  // Ensure that notifications is an array
  const notifications =
    NotificationsResponse && Array.isArray(NotificationsResponse.data)
      ? NotificationsResponse.data
      : [];
  const unreadNotifications =
    notifications?.filter((notification) => !notification.isRead) || [];

  // Set indicator to true if there are any unread notifications
  const hasUnreadNotifications = unreadNotifications.length > 0;

  return (
    <Dropdown
      indicator={hasUnreadNotifications} // Indicator is true if there are unread notifications
      title="Notifications"
      orientation="end"
      toggleImage={<NotificationIcon />}
      toggleClassNames="rounded-full text-slate-400 hover:text-red-500 hover:bg-red-500/10 dark:hover:bg-red-500/20 transition-colors duration-300"
      onClose={handleDropdownClose} // Handle dropdown close event
    >
      <div className="px-2 pt-2">
        {unreadNotifications?.length > 0 ? (
          unreadNotifications?.map((notification) => (
            <div
              key={notification.id}
              className="flex cursor-pointer gap-3.5 px-4 py-2.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-900 transition-colors duration-300"
              onClick={() => readNotificationsMutation.mutate()}
            >
              <div className="relative flex h-9 w-9 min-w-[36px] items-center bg-white dark:bg-slate-950 justify-center rounded-full border border-slate-200 shadow-lg shadow-slate-300/30 dark:border-slate-800 dark:shadow-slate-800/30">
                <NotificationItemIcon />
              </div>
              <div>
                <p className="text-sm leading-snug text-slate-500">
                  <a className="cursor-pointer font-medium text-red-500 hover:underline">
                    {notification.title}
                  </a>{" "}
                  {notification.message}
                </p>
                <small className="text-xs text-slate-400">
                  {formatDistanceToNow(new Date(notification.createdAt), {
                    addSuffix: true,
                  })}
                </small>
              </div>
            </div>
          ))
        ) : (
          <div className="flex flex-col items-center justify-center gap-4">
            <NotificationItemIcon aspects="40" />
            <p className="text-sm text-center text-slate-500">
              There are no new notifications.
            </p>
          </div>
        )}
      </div>
    </Dropdown>
  );
};

export default NotificationsDropdown;
